import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import api from 'api';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import {
  EmptyIndicator,
  LoadingIndicator,
  MoreOptions,
  Pagination
} from 'components/common';
import { AdminGroupAvatar } from 'components/common/admins';
import { Box, BoxBackLink } from 'components/common/box';
import { GroupUserAttributes } from 'components/groups';
import { AdminGroupsRemove, AdminGroupsUpdate, AdminGroupUsers } from '.';

import type { AdminGroup as IAdminGroup } from 'shared-types';

export type AdminGroupUpdateUsers = {
  page?: number;
  search?: string;
  sortBy?: string;
};

const AdminGroup = () => {
  const {
    messages,
    pageState: { adminGroupId }
  } = useReduxState();

  const { data, loading, updateData } = useApiData({
    onlyIf: Boolean(adminGroupId),
    url: `/admin/admin-groups?id=${adminGroupId}`
  });

  const adminGroup = data?.adminGroups?.[0] as IAdminGroup;

  const [users, setUsers] = useState([]);
  const [state, setState] = useState({
    page: 1,
    pages: 1,
    search: '',
    sortBy: ''
  });

  useEffect(() => {
    getUsers({});
  }, [adminGroup]);

  const getUsers = async ({ page, search, sortBy }: AdminGroupUpdateUsers) => {
    try {
      const pageValue = page !== undefined ? page : state.page;
      const searchValue = search !== undefined ? search : state.search;
      const sortByValue = sortBy !== undefined ? sortBy : state.sortBy;

      const response = await api.get(
        `/admin/admin-groups/${adminGroup.id}/users?page=${pageValue}&search=${searchValue}&sortBy=${sortByValue}`
      );
      setUsers(response.data.users);
      setState({
        page: pageValue,
        pages: response.data.pages,
        search: searchValue,
        sortBy: sortByValue
      });
    } catch (err) {}
  };

  const handleUpdateUsers = async (props: AdminGroupUpdateUsers) => {
    await getUsers(props);
    await updateData();
  };

  const handleAddOrEditUserAttribute = async (
    newConditions: typeof adminGroup.userAttributes[0],
    conditionsIndex?: number
  ) => {
    await api.put(`/admin/admin-groups/${adminGroup.id}`, {
      userAttributes:
        conditionsIndex !== undefined
          ? [
              ...adminGroup.userAttributes.slice(0, conditionsIndex),
              newConditions,
              ...adminGroup.userAttributes.slice(conditionsIndex + 1)
            ]
          : [...adminGroup.userAttributes, newConditions]
    });
    await updateData();
  };

  const handleRemoveUserAttribute = async (attributeIndex: number) => {
    try {
      await api.put(`/admin/admin-groups/${adminGroup.id}`, {
        userAttributes: adminGroup.userAttributes.filter(
          (attr, i) => i !== attributeIndex
        )
      });
      await updateData();
      return true;
    } catch (err) {
      return false;
    }
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <div>
      <Box
        headerContent={
          <BoxBackLink
            as="/admin/admin-groups"
            href="/admin/admin-groups"
            text={messages.adminGroupsAll}
          />
        }
      >
        {adminGroup ? (
          adminGroup.isEditable ? (
            <div className="group-info">
              <AdminGroupAvatar adminGroup={adminGroup} enableLink={false} />
              <div className="group-actions">
                <MoreOptions styleContentButtons={false}>
                  <AdminGroupsUpdate
                    adminGroup={adminGroup}
                    updateGroups={updateData}
                  />
                  <AdminGroupsRemove adminGroupId={adminGroup.id} />
                </MoreOptions>
              </div>
            </div>
          ) : (
            <EmptyIndicator
              description={messages.adminGroupNonEditable}
              icon="lock"
              title={
                adminGroup.id === 1
                  ? messages.adminGroupsAllAdmins
                  : adminGroup.title
              }
            />
          )
        ) : (
          <EmptyIndicator icon="users" title={messages.adminGroupNotFound} />
        )}
      </Box>

      {adminGroup && adminGroup.isEditable ? (
        <>
          <div className="boxes">
            <AdminGroupUsers users={users} updateUsers={handleUpdateUsers} />

            <Pagination
              currentPage={state.page}
              numberOfPages={state.pages}
              onPageChange={getUsers}
            />
          </div>

          <div className="boxes">
            <GroupUserAttributes
              onAddUserAttribute={handleAddOrEditUserAttribute}
              onEditUserAttribute={handleAddOrEditUserAttribute}
              onRemoveUserAttribute={handleRemoveUserAttribute}
              tooltip={messages.adminGroupUserAttributesTooltip}
              userAttributes={adminGroup.userAttributes}
            />
          </div>
        </>
      ) : null}

      <style jsx>{`
        .boxes {
          margin-top: 40px;
        }
        .group-info {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
        }
        .group-actions {
          display: flex;
          gap: 10px;
        }
        @media (max-width: ${breakpoints.small}px) {
          .group-actions {
            flex-direction: column;
            margin: 5px 0;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminGroup;
